// ---------------------------------------------------------------------------
// Marketplace + Directory hooks
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback, useMemo } from "react";
import { apiFetch } from "@/lib/api-client";

export interface MarketplacePackage {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  tags: string[];
  priceCents: number;
  currency: string;
  creatorName: string;
  creatorTenantId: string;
  avgRating: number;
  reviewCount: number;
  installCount: number;
  performancePreview: {
    avgConversionRatePercent: number | null;
    avgEmailOpenRatePercent: number | null;
    avgRoas: number | null;
    sampleSize: number;
    isVerified: boolean;
  } | null;
  packageSummary: {
    assetCounts: Record<string, number>;
    totalAssets: number;
    highlights: string[];
  };
  previewState: {
    status: "available" | "unavailable";
    label: string;
    description: string;
  };
  installState: {
    status: "preview_only" | "checkout_required" | "owned_by_creator" | "already_purchased" | "already_deployed" | "install_available";
    label: string;
    description: string;
    canPreview: boolean;
    canInstall: boolean;
  };
  publishedAt: string | null;
}

export interface MarketplacePackageDetail extends MarketplacePackage {
  snapshotId: string;
  snapshotVersionId: string;
  canonicalModel: {
    kind: "marketplace_package";
    listingId: string;
    snapshotId: string;
    snapshotVersionId: string;
    sourceTemplateKind: "personal_template";
    descriptor: {
      kind: "marketplace_package";
      label: string;
      description: string;
      channel: "marketplace";
      granularity: "snapshot_bundle";
      installMode: "deploy_snapshot";
      visibility: "marketplace_public";
      isPrivate: boolean;
      isPublishable: boolean;
      isDistributable: boolean;
      publishTarget: "marketplace_package";
      sourceOfTruth: "marketplace_listing";
      legacySurfaces: string[];
    };
  };
  purchaseSemantics: {
    purchasedSnapshotId: string;
    purchasedSnapshotVersionId: string;
    assetOwnership: "buyer_owned_copy";
    creatorUpdatePropagation: "manual_version_offer";
    existingBuyerInstallsOnNewVersion: "unchanged";
    legacyKitFlowStatus: "bridged_to_marketplace";
  };
  assetCounts: Record<string, number>;
  screenshots: string[];
  creatorProfile: {
    name: string;
    totalListings: number;
    avgRating: number;
  };
  recentReviews: Array<{
    rating: number;
    text: string | null;
    createdAt: string;
  }>;
  viewerReview: {
    rating: number;
    text: string | null;
    createdAt: string;
  } | null;
  reviewState: {
    canReview: boolean;
    hasReviewed: boolean;
    label: string;
    description: string;
  };
  verifiedPerformance: {
    totalInstalls: number;
    conversionRate: { avg: number; median: number; p90: number } | null;
    emailOpenRate: { avg: number; median: number; p90: number } | null;
    roas: { avg: number; median: number } | null;
    topPerformingAsset: { name: string; metric: string; value: number } | null;
    isVerified: boolean;
    lastComputedAt: string;
  };
}

export interface MarketplacePreview {
  snapshotId: string;
  versionNumber: number;
  configHash: string;
  assetCounts: Record<string, number>;
  items: Array<{
    assetType: string;
    assetId: string;
    name: string;
    description?: string;
    included: boolean;
  }>;
  configSizeBytes: number;
  mode: "snapshot_contents";
  description: string;
}

export interface MarketplaceBrowseData {
  packages: MarketplacePackage[];
  total: number;
  categories: Array<{ name: string; count: number }>;
  featured: MarketplacePackage[];
}

function buildMarketplaceBrowseData(
  packages: MarketplacePackage[],
  selectedCategory?: string,
  search?: string,
): MarketplaceBrowseData {
  const filtered = packages.filter((pkg) => {
    const matchesCategory = !selectedCategory || pkg.category === selectedCategory;
    if (!matchesCategory) return false;

    const query = search?.trim().toLowerCase();
    if (!query) return true;

    return [
      pkg.title,
      pkg.description ?? "",
      pkg.category ?? "",
      pkg.creatorName,
      ...pkg.tags,
      ...pkg.packageSummary.highlights,
    ].some((value) => value.toLowerCase().includes(query));
  });

  const categoryCounts = new Map<string, number>();
  for (const pkg of packages) {
    const category = pkg.category ?? "General";
    categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1);
  }

  return {
    packages: filtered,
    total: filtered.length,
    categories: [...categoryCounts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((left, right) => left.name.localeCompare(right.name)),
    featured: selectedCategory || search
      ? []
      : [...packages]
        .sort((left, right) => {
          return right.installCount - left.installCount
            || right.avgRating - left.avgRating
            || right.packageSummary.totalAssets - left.packageSummary.totalAssets;
        })
        .slice(0, 3),
  };
}

export function useMarketplaceBrowse(category?: string, search?: string) {
  const [data, setData] = useState<MarketplaceBrowseData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("limit", "50");
    if (category) params.set("category", category);
    if (search) params.set("q", search);

    setIsLoading(true);
    setError(null);
    apiFetch<{ data: MarketplacePackage[] }>(`/v1/marketplace/packages?${params.toString()}`)
      .then((res) => setData(buildMarketplaceBrowseData(res.data ?? [], category, search)))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"))
      .finally(() => setIsLoading(false));
  }, [category, search]);

  return { data, isLoading, error };
}

export function useMarketplacePackageDetail(packageId?: string) {
  const [data, setData] = useState<MarketplacePackageDetail | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(packageId));
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!packageId) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: MarketplacePackageDetail }>(`/v1/marketplace/packages/${packageId}`);
      setData(res.data);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, [packageId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, isLoading, error, refresh, setData };
}

export function useMarketplacePackagePreview(packageId?: string) {
  const [data, setData] = useState<MarketplacePreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPreview = useCallback(async () => {
    if (!packageId) return null;

    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: MarketplacePreview }>(`/v1/marketplace/packages/${packageId}/preview`);
      setData(res.data);
      return res.data;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
      setData(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [packageId]);

  return { data, isLoading, error, loadPreview };
}

export interface InstallProgress {
  status: "preparing" | "installing" | "configuring" | "complete" | "failed";
  currentStep: string;
  stepsComplete: number;
  stepsTotal: number;
  assetsCreated: string[];
  error: string | null;
}

export interface MarketplaceCheckoutSession {
  providerId: "stripe" | "paypal" | "mollie" | "maib_bank";
  connectionId: string;
  providerSessionId: string;
  checkoutUrl: string | null;
  status: "pending" | "requires_action" | "processing" | "completed" | "expired" | "cancelled";
  expiresAt: string | null;
}

export interface MarketplaceInstallResult {
  purchaseId: string;
  transactionRef: string | null;
  deployResult: "deployed" | "already_deployed" | "error";
  revenueShareId: string | null;
  assetCounts: Record<string, number>;
  canonicalModel: Record<string, unknown>;
  purchaseSemantics: Record<string, unknown>;
  installState: MarketplacePackage["installState"] | null;
}

export function useInstallPackage() {
  const [progress, setProgress] = useState<InstallProgress | null>(null);
  const [isInstalling, setIsInstalling] = useState(false);

  const install = useCallback(async (packageId: string, excludeAssetIds: string[] = []) => {
    setIsInstalling(true);
    setProgress({
      status: "preparing",
      currentStep: "Sending install request",
      stepsComplete: 0,
      stepsTotal: 3,
      assetsCreated: [],
      error: null,
    });

    try {
      setProgress({
        status: "installing",
        currentStep: "Installing package assets",
        stepsComplete: 1,
        stepsTotal: 3,
        assetsCreated: [],
        error: null,
      });

      const res = await apiFetch<{
        data?: MarketplaceInstallResult & { assetsCreated?: string[] };
        error?: string;
      }>(`/v1/marketplace/packages/${packageId}/install`, {
        method: "POST",
        body: JSON.stringify({
          stripePaymentMethodId: null,
          excludeAssetIds,
        }),
      });

      setProgress({
        status: "complete",
        currentStep: "Install complete",
        stepsComplete: 3,
        stepsTotal: 3,
        assetsCreated: res.data?.assetsCreated ?? [],
        error: null,
      });
      return res.data ?? null;
    } catch (err) {
      setProgress({
        status: "failed",
        currentStep: "Install failed",
        stepsComplete: 0,
        stepsTotal: 3,
        assetsCreated: [],
        error: err instanceof Error ? err.message : "Failed to install package",
      });
      throw err;
    } finally {
      setIsInstalling(false);
    }
  }, []);

  return { install, progress, isInstalling };
}

export async function startMarketplaceCheckout(
  packageId: string,
  excludeAssetIds: string[] = [],
): Promise<MarketplaceCheckoutSession> {
  const response = await apiFetch<{ data: MarketplaceCheckoutSession }>(`/v1/marketplace/packages/${packageId}/checkout`, {
    method: "POST",
    body: JSON.stringify({ excludeAssetIds }),
  });

  return response.data;
}

export async function completeMarketplaceCheckout(
  packageId: string,
  providerId: MarketplaceCheckoutSession["providerId"],
  providerSessionId: string,
  excludeAssetIds: string[] = [],
): Promise<MarketplaceInstallResult> {
  const response = await apiFetch<{ data: MarketplaceInstallResult }>(`/v1/marketplace/packages/${packageId}/complete-checkout`, {
    method: "POST",
    body: JSON.stringify({
      providerId,
      providerSessionId,
      excludeAssetIds,
    }),
  });

  return response.data;
}

export interface MarketplaceReviewSubmission {
  rating: number;
  reviewText?: string;
}

export async function submitMarketplaceReview(
  packageId: string,
  input: MarketplaceReviewSubmission,
): Promise<MarketplacePackageDetail> {
  const response = await apiFetch<{ data: MarketplacePackageDetail }>(`/v1/marketplace/packages/${packageId}/reviews`, {
    method: "POST",
    body: JSON.stringify(input),
  });

  return response.data;
}

export interface CreatorPackage {
  id: string;
  name: string;
  status: "draft" | "review" | "published" | "rejected" | "archived" | "suspended";
  version: string;
  installs: number;
  revenue: number;
  rating: number;
  reviewCount: number;
  createdAt: string;
  updatedAt: string;
  moderationReason: string | null;
  reviewedAt: string | null;
  publishedAt: string | null;
  lifecycle: {
    label: string;
    description: string;
    availableActions: Array<"submit_for_review" | "withdraw_to_draft" | "archive_listing">;
  };
  listingPath: string | null;
  recentReviews: Array<{
    rating: number;
    text: string | null;
    createdAt: string;
  }>;
  reviewPipeline: {
    pendingCount: number;
    lastPendingSubmittedAt: string | null;
  };
  installAdoption: {
    deployedCount: number;
    pendingSetupCount: number;
    lastPurchasedAt: string | null;
    lastDeployedAt: string | null;
  };
}

export interface CreatorDashboardData {
  packages: CreatorPackage[];
  totalRevenue: number;
  totalInstalls: number;
  averageRating: number;
  revenueByMonth: Array<{ month: string; revenue: number }>;
  lifecycleSummary: {
    liveOffers: number;
    inReview: number;
    needsAttention: number;
    archived: number;
    thisMonthRevenue: number;
    pendingBuyerReviews: number;
    pendingBuyerSetup: number;
    deployedInstalls: number;
    topPerformer: {
      id: string;
      name: string;
      installs: number;
      revenue: number;
      rating: number;
    } | null;
  };
  payoutSummary: {
    pendingPayout: number;
    paidOutTotal: number;
    lastPaidAt: string | null;
    lastPayoutBatchId: string | null;
  };
  payoutHistory: Array<{
    payoutBatchId: string;
    paidAt: string | null;
    amount: number;
    currency: string;
    shareCount: number;
  }>;
  publishingSurface: {
    status: "submission_mounted";
    label: string;
    description: string;
  };
}

export function useCreatorDashboard() {
  const [data, setData] = useState<CreatorDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const res = await apiFetch<{ data: CreatorDashboardData }>("/v1/marketplace/creator/dashboard");
      setData(res.data);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, isLoading, error, refresh, setData };
}

// ---------------------------------------------------------------------------
// F-149 — Creator revenue + payouts focused read fetchers
// ---------------------------------------------------------------------------
// The existing `useCreatorDashboard` hook fans out into ONE blob covering
// six tiles. F-149 ships two focused endpoints so AI agents (and future
// React surfaces) can pull just the revenue OR just the payout history
// without paying for the whole dashboard payload, and so a CQRS audit
// event is emitted scoped to which surface was opened.
// ---------------------------------------------------------------------------

export interface CreatorRevenueListingRow {
  listingId: string;
  title: string;
  status: string;
  revenue: number;
  installs: number;
  lastSaleAt: string | null;
}

export interface CreatorRevenueResponse {
  creatorId: string;
  currency: string;
  totalRevenue: number;
  totalRevenueMinor: number;
  thisMonthRevenue: number;
  totalInstalls: number;
  revenueByMonth: Array<{ month: string; revenue: number }>;
  listings: CreatorRevenueListingRow[];
  viewedAt: string;
}

export interface CreatorPayoutHistoryRow {
  payoutBatchId: string;
  paidAt: string | null;
  amount: number;
  amountMinor: number;
  currency: string;
  shareCount: number;
}

export interface CreatorPayoutsResponse {
  creatorId: string;
  currency: string;
  pendingPayout: number;
  pendingPayoutMinor: number;
  paidOutTotal: number;
  paidOutTotalMinor: number;
  lastPaidAt: string | null;
  lastPayoutBatchId: string | null;
  payoutsEnabled: boolean;
  stripeConnectAccountId: string | null;
  history: CreatorPayoutHistoryRow[];
  viewedAt: string;
}

export async function fetchCreatorRevenue(
  creatorId: string,
): Promise<CreatorRevenueResponse> {
  const res = await apiFetch<{ data: CreatorRevenueResponse }>(
    `/v1/marketplace/creator/${encodeURIComponent(creatorId)}/revenue`,
  );
  return res.data;
}

export async function fetchCreatorPayouts(
  creatorId: string,
): Promise<CreatorPayoutsResponse> {
  const res = await apiFetch<{ data: CreatorPayoutsResponse }>(
    `/v1/marketplace/creator/${encodeURIComponent(creatorId)}/payouts`,
  );
  return res.data;
}

export function useCreatorRevenue(creatorId: string | undefined) {
  const [data, setData] = useState<CreatorRevenueResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(creatorId));
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!creatorId) {
      setData(null);
      setIsLoading(false);
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      setData(await fetchCreatorRevenue(creatorId));
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, [creatorId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, isLoading, error, refresh };
}

export function useCreatorPayouts(creatorId: string | undefined) {
  const [data, setData] = useState<CreatorPayoutsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(creatorId));
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!creatorId) {
      setData(null);
      setIsLoading(false);
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      setData(await fetchCreatorPayouts(creatorId));
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, [creatorId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, isLoading, error, refresh };
}

export async function updateCreatorPackageLifecycle(
  packageId: string,
  action: "submit_for_review" | "withdraw_to_draft" | "archive_listing",
): Promise<CreatorPackage> {
  const response = await apiFetch<{ data: { action: string; package: CreatorPackage | null } }>(
    `/v1/marketplace/creator/packages/${packageId}/lifecycle`,
    {
      method: "POST",
      body: JSON.stringify({ action }),
    },
  );

  if (!response.data.package) {
    throw new Error("Updated package payload missing from creator lifecycle response.");
  }

  return response.data.package;
}

export async function publishPackage(): Promise<void> {
  throw new Error("Use updateCreatorPackageLifecycle() with the mounted marketplace submission lifecycle.");
}

export interface MarketplaceReviewQueueEntry {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  creatorTenantId: string;
  creatorName: string;
  submittedAt: string;
  updatedAt: string;
  packageSummary: {
    assetCounts: Record<string, number>;
    totalAssets: number;
    highlights: string[];
  };
}

export interface MarketplaceBuyerReviewQueueEntry {
  id: string;
  listingId: string;
  listingTitle: string;
  category: string | null;
  creatorTenantId: string;
  creatorName: string;
  reviewerTenantId: string;
  rating: number;
  reviewText: string | null;
  submittedAt: string;
}

export interface MarketplaceRevenueShareQueueEntry {
  creatorTenantId: string;
  creatorName: string;
  pendingPayout: number;
  currency: string;
  shareCount: number;
  oldestPendingAt: string;
  newestPendingAt: string;
}

export interface MarketplaceRevenueSharePayoutProcessResult {
  payoutBatchId: string | null;
  totalCreators: number;
  totalShares: number;
  totalAmount: number;
  processedCreators: Array<{
    creatorTenantId: string;
    amount: number;
    currency: string;
    shareCount: number;
  }>;
}

export interface MarketplaceGovernanceListingEntry {
  id: string;
  title: string;
  category: string | null;
  creatorTenantId: string;
  creatorName: string;
  status: "published" | "suspended";
  visibility: "public" | "private";
  installCount: number;
  rating: number;
  reviewCount: number;
  publishedAt: string | null;
  updatedAt: string;
  moderationReason: string | null;
}

export function useMarketplaceReviewQueue() {
  const [entries, setEntries] = useState<MarketplaceReviewQueueEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const res = await apiFetch<{ data: MarketplaceReviewQueueEntry[] }>("/v1/marketplace/review-queue");
      setEntries(res.data ?? []);
    } catch (err) {
      setEntries([]);
      setError(err instanceof Error ? err.message : "Failed to load marketplace review queue");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { entries, isLoading, error, refresh, setEntries };
}

export function useMarketplaceBuyerReviewQueue() {
  const [entries, setEntries] = useState<MarketplaceBuyerReviewQueueEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const res = await apiFetch<{ data: MarketplaceBuyerReviewQueueEntry[] }>("/v1/marketplace/review-queue/reviews");
      setEntries(res.data ?? []);
    } catch (err) {
      setEntries([]);
      setError(err instanceof Error ? err.message : "Failed to load marketplace buyer review queue");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { entries, isLoading, error, refresh, setEntries };
}

export function useMarketplaceRevenueShareQueue() {
  const [entries, setEntries] = useState<MarketplaceRevenueShareQueueEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const res = await apiFetch<{ data: MarketplaceRevenueShareQueueEntry[] }>("/v1/marketplace/review-queue/payouts");
      setEntries(res.data ?? []);
    } catch (err) {
      setEntries([]);
      setError(err instanceof Error ? err.message : "Failed to load marketplace creator payout queue");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { entries, isLoading, error, refresh, setEntries };
}

export function useMarketplaceGovernanceListings() {
  const [entries, setEntries] = useState<MarketplaceGovernanceListingEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const res = await apiFetch<{ data: MarketplaceGovernanceListingEntry[] }>("/v1/marketplace/review-queue/listings");
      setEntries(res.data ?? []);
    } catch (err) {
      setEntries([]);
      setError(err instanceof Error ? err.message : "Failed to load marketplace governance listings");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { entries, isLoading, error, refresh, setEntries };
}

export async function reviewMarketplaceQueueEntry(
  packageId: string,
  action: "approve" | "reject",
  feedback?: string,
): Promise<{
  type: "updated";
  listingId: string;
  creatorTenantId: string;
  status: "published" | "archived";
  visibility: "public" | "private";
}> {
  const response = await apiFetch<{
    data: {
      type: "updated";
      listingId: string;
      creatorTenantId: string;
      status: "published" | "archived";
      visibility: "public" | "private";
    };
  }>(`/v1/marketplace/review-queue/${packageId}/decision`, {
    method: "POST",
    body: JSON.stringify({ action, feedback }),
  });

  return response.data;
}

export async function reviewMarketplaceBuyerReviewEntry(
  reviewId: string,
  action: "approve" | "reject",
  feedback?: string,
): Promise<{
  type: "updated";
  reviewId: string;
  listingId: string;
  reviewerTenantId: string;
  status: "approved" | "removed";
}> {
  const response = await apiFetch<{
    data: {
      type: "updated";
      reviewId: string;
      listingId: string;
      reviewerTenantId: string;
      status: "approved" | "removed";
    };
  }>(`/v1/marketplace/review-queue/reviews/${reviewId}/decision`, {
    method: "POST",
    body: JSON.stringify({ action, feedback }),
  });

  return response.data;
}

export async function processMarketplaceRevenueSharePayouts(): Promise<MarketplaceRevenueSharePayoutProcessResult> {
  const response = await apiFetch<{ data: MarketplaceRevenueSharePayoutProcessResult }>(
    "/v1/marketplace/review-queue/payouts/process",
    {
      method: "POST",
    },
  );

  return response.data;
}

export async function updateMarketplaceListingLifecycle(
  packageId: string,
  action: "suspend" | "restore",
  feedback?: string,
): Promise<{
  type: "updated";
  listingId: string;
  creatorTenantId: string;
  status: "published" | "suspended";
  visibility: "public" | "private";
  moderationReason: string | null;
}> {
  const response = await apiFetch<{
    data: {
      type: "updated";
      listingId: string;
      creatorTenantId: string;
      status: "published" | "suspended";
      visibility: "public" | "private";
      moderationReason: string | null;
    };
  }>(`/v1/marketplace/review-queue/listings/${packageId}/lifecycle`, {
    method: "POST",
    body: JSON.stringify({ action, feedback }),
  });

  return response.data;
}

export interface AgencyProfile {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  specialties: string[];
  verticals: string[];
  location: string;
  rating: number;
  reviewCount: number;
  clientCount: number;
  certifications: Array<{ name: string; level: "bronze" | "silver" | "gold" | "platinum" }>;
  contactUrl?: string;
  featured: boolean;
  verifiedPerformance: {
    isVerified: boolean;
    avgClientRetentionMonths: number | null;
    totalManagedRevenueCents: number;
  };
}

interface AgencyDirectoryApiListing {
  orgId: string;
  name: string;
  slug: string;
  specialty: string | null;
  vertical: string | null;
  region: string | null;
  language: string | null;
  verifiedMetrics: {
    clientCount: number;
    avgRating: number | null;
    avgClientRetentionMonths: number | null;
    totalManagedRevenueCents: number;
  };
  certifications?: Array<{ name: string; level: "bronze" | "silver" | "gold" | "platinum" }>;
  logoUrl: string | null;
  description: string | null;
  isVerified: boolean | string | null;
}

function splitDirectoryLabels(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }

  return value
    .split(/[,|/]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function matchesDirectorySearch(agency: AgencyProfile, search: string | undefined): boolean {
  if (!search) {
    return true;
  }

  const needle = search.trim().toLowerCase();
  if (!needle) {
    return true;
  }

  return [
    agency.name,
    agency.description,
    agency.location,
    ...agency.specialties,
    ...agency.verticals,
  ].some((value) => value.toLowerCase().includes(needle));
}

function mapAgencyDirectoryListing(listing: AgencyDirectoryApiListing): AgencyProfile {
  const isVerified = listing.isVerified === true || listing.isVerified === "true";
  const specialties = splitDirectoryLabels(listing.specialty);
  const verticals = splitDirectoryLabels(listing.vertical);
  const certifications = listing.certifications?.length
    ? listing.certifications
    : isVerified
      ? [{ name: "Verified Partner", level: "gold" as const }]
      : [];

  return {
    id: listing.orgId,
    name: listing.name,
    description: listing.description ?? "Certified Stero partner",
    logoUrl: listing.logoUrl ?? undefined,
    specialties,
    verticals,
    location: listing.region?.trim() || "Global",
    rating: listing.verifiedMetrics.avgRating ?? 0,
    reviewCount: 0,
    clientCount: listing.verifiedMetrics.clientCount,
    certifications,
    contactUrl: undefined,
    featured: isVerified,
    verifiedPerformance: {
      isVerified,
      avgClientRetentionMonths: listing.verifiedMetrics.avgClientRetentionMonths,
      totalManagedRevenueCents: listing.verifiedMetrics.totalManagedRevenueCents,
    },
  };
}

export function useAgencyDirectory(vertical?: string, search?: string) {
  const [directoryListings, setDirectoryListings] = useState<AgencyProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const params = new URLSearchParams();
    if (vertical) params.set("vertical", vertical);
    const qs = params.toString() ? `?${params.toString()}` : "";
    apiFetch<{ data: AgencyDirectoryApiListing[] }>(`/platform/directory${qs}`)
      .then((res) => setDirectoryListings((res.data ?? []).map(mapAgencyDirectoryListing)))
      .catch(() => setDirectoryListings([]))
      .finally(() => setIsLoading(false));
  }, [vertical]);

  const agencies = useMemo(
    () => directoryListings.filter((agency) => matchesDirectorySearch(agency, search)),
    [directoryListings, search],
  );

  return { agencies, isLoading };
}
